/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/VideoTutorialSectionModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styles from "./index.module.css";
import {
  createVideoSection,
  updateVideoSection,
} from "../../logic/videoTutorialSection/index.js";

const VideoTutorialSectionModal = ({
  open,
  onClose,
  section,
  mode = "create",
  onSectionCreated,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dialogRef = useRef(null);
  const isEditMode = mode === "edit";

  const [sectionName, setSectionName] = useState(section?.name || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [originalSectionName, setOriginalSectionName] = useState("");

  useEffect(() => {
    if (open && isEditMode && section) {
      const initialSectionName = section.name || "";
      setSectionName(initialSectionName);
      setOriginalSectionName(initialSectionName);
      setError("");
    } else if (open && !isEditMode) {
      setSectionName("");
      setOriginalSectionName("");
      setError("");
    }
  }, [open, isEditMode, section]);

  useEffect(() => {
    const handleClose = () => {
      setOriginalSectionName("");
      onClose();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      dialog.showModal();
    } else {
      if (dialog) {
        dialog.close();
      }
      setOriginalSectionName("");
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onClose]);

  const handleCancel = () => {
    setOriginalSectionName("");
    onClose();
  };

  const hasChanges = () => {
    if (!isEditMode) {
      return sectionName.trim().length > 0;
    }
    return sectionName.trim() !== originalSectionName.trim();
  };

  const isFormValid = () => {
    return sectionName.trim().length > 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      return;
    }

    setLoading(true);
    setError("");
    try {
      let sectionData;
      if (isEditMode) {
        sectionData = await updateVideoSection(
          section._id,
          { name: sectionName },
          navigate,
        );
      } else {
        sectionData = await createVideoSection({ name: sectionName }, navigate);
      }

      if (!sectionData) {
        logger.error("Failed to create/update section");
        setError(
          isEditMode
            ? t("components.videoTutorialSectionModal.updateError")
            : t("components.videoTutorialSectionModal.createError"),
        );
        setLoading(false);
        return;
      }

      onClose();
      if (onSectionCreated) {
        onSectionCreated();
      }
    } catch (error) {
      logger.error("Error saving section:", { err: sanitizeError(error) });
      setError(
        isEditMode
          ? t("components.videoTutorialSectionModal.updateError")
          : t("components.videoTutorialSectionModal.createError"),
      );
    } finally {
      setLoading(false);
    }
  };

  const getModalTitle = () => {
    return isEditMode
      ? t("components.videoTutorialSectionModal.editTitle")
      : t("components.videoTutorialSectionModal.createTitle");
  };

  const getSubtitle = () => {
    return isEditMode
      ? t("components.videoTutorialSectionModal.editSubtitle")
      : t("components.videoTutorialSectionModal.subtitle");
  };

  return (
    <>
      {open && <div className={styles.overlay}></div>}
      <dialog ref={dialogRef} className={styles.modal}>
        <div className={styles.closeButtonContainer}>
          <button className={styles.closeButton} onClick={handleCancel}>
            &times;
          </button>
        </div>

        <div className={styles.modalContent}>
          <label className={styles.modalTitle}>{getModalTitle()}</label>
          <p className={styles.modalSubtitle}>{getSubtitle()}</p>

          {error && <div className={styles.errorMessage}>{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className={styles.formContainer}>
              {/* Section Name */}
              <div className={styles.inputGroup}>
                <label className={styles.label}>
                  {t("components.videoTutorialSectionModal.sectionName")}
                </label>
                <input
                  type="text"
                  className={styles.input}
                  placeholder={t(
                    "components.videoTutorialSectionModal.sectionNamePlaceholder",
                  )}
                  value={sectionName}
                  onChange={(e) => setSectionName(e.target.value)}
                />
              </div>
            </div>

            {/* Modal Buttons */}
            <div className={styles.modalButtons}>
              <button
                type="button"
                onClick={handleCancel}
                className={styles.cancelButton}
                disabled={loading}
              >
                {t("components.videoTutorialSectionModal.cancelButton")}
              </button>
              <button
                type="submit"
                className={styles.saveButton}
                disabled={
                  loading || !isFormValid() || (isEditMode && !hasChanges())
                }
              >
                {loading
                  ? t("components.videoTutorialSectionModal.saving")
                  : t("components.videoTutorialSectionModal.saveButton")}
              </button>
            </div>
          </form>
        </div>
      </dialog>
    </>
  );
};

export default VideoTutorialSectionModal;
